import { CheckIcon } from '@heroicons/react/24/outline'
import { useFormik } from 'formik'
import PropTypes from 'prop-types'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import * as Yup from 'yup'

import PersonHeart from '../assets/images/illustrations/person-heart.png'
import { Seo } from '../components'
import Alert from '../components/alert'
import Input from '../components/input'
import Button from '../components/tailwind/Button'
import PageHeading from '../components/tailwind/PageHeading'
import Textarea from '../components/textarea'
import { usePartnerContext, useUserContext } from '../context'
import { mutation } from '../graphql'
import {
  collectLogsForSupport,
  createSupportEmailWithLogs
} from '../helpers/log-collector'
const { version } = require('../../package.json')

const TRANSLATION_LOCATION = 'pages.support.'

const CATEGORIES = ['question', 'problem', 'access', 'billing', 'other']
const MAX_SUBJECT_LENGTH = 120
const MAX_MESSAGE_LENGTH = 4000
const MIN_MESSAGE_LENGTH = 10

const Success = ({ email }) => {
  const { t } = useTranslation()

  return (
    <div className='xbg-pink-200 xborder-l-4 rounded-2xl bg-green-50 p-6 sm:flex'>
      <div className='hidden h-12 w-12 items-center justify-center rounded-full bg-green-100 sm:flex'>
        <CheckIcon className='h-6 w-6 text-green-600' aria-hidden='true' />
      </div>
      <div className='sm:ml-4'>
        <h3 className='text-md font-medium leading-6 text-gray-900'>
          {t(TRANSLATION_LOCATION + 'message_sent_success_title')}
        </h3>
        <p className='text-sm text-gray-700'>
          {t(TRANSLATION_LOCATION + 'message_sent_success_description')}{' '}
          {email &&
            t(TRANSLATION_LOCATION + 'message_sent_reply_to', { email })}
        </p>
      </div>
    </div>
  )
}

Success.propTypes = { email: PropTypes.string }

const getName = (person) => {
  const firstname = person.firstname || person.invitation_firstname
  const lastname = person.lastname || person.invitation_lastname

  if (!firstname) return lastname
  if (!lastname) return firstname

  return `${firstname} ${lastname}`
}

const Support = () => {
  const { t } = useTranslation()
  const { partner } = usePartnerContext()
  const { activeSpace } = useUserContext()

  const person_email =
    activeSpace.person.email || activeSpace.person.invitation_email

  const [messageSent, setMessageSent] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [includeLogs, setIncludeLogs] = useState(false)
  const [sendError, setSendError] = useState(false)

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      category: 'question',
      subject: '',
      message: ''
    },
    onSubmit: async ({ category, subject, message }) => {
      setIsLoading(true)
      setSendError(false)

      try {
        const categoryLabel = t(TRANSLATION_LOCATION + `category_${category}`)

        // Der Slack-Post zeigt Betreff und Inhalt, deshalb stehen die
        // wichtigsten Kontextdaten kurz und lesbar im Inhalt.
        let content = message
        content += '\n\n--- META ---'
        content += `\nKategorie: ${categoryLabel}`
        content += `\nPartner: ${partner.name} (${partner.id})`
        content += `\nVon: ${getName(activeSpace.person)} <${person_email}>`
        content += `\nBrowser: ${navigator?.userAgent}`
        content += `\nDashboard-Version: ${version}`

        if (includeLogs) {
          content += `\n\n--- DEBUG INFORMATION ---\n${collectLogsForSupport()}`
        }

        await mutation({
          // no auth rules specified
          mutation: 'createForm',
          input: {
            partner_id: partner.id,
            data: JSON.stringify({
              EMAIL: person_email,
              properties: {
                content,
                hs_pipeline: '0',
                hs_pipeline_stage: '1',
                subject: `[${categoryLabel}] ${subject.trim()}`
              }
            }),
            form_source: 'HUBSPOT_SUPPORT_TICKET'
          }
        })

        setMessageSent(true)
        formik.resetForm()
      } catch (err) {
        // Eingabe bleibt stehen, der User kann es direkt erneut versuchen
        setSendError(true)
      } finally {
        setIsLoading(false)
      }
    },
    validationSchema: Yup.object({
      subject: Yup.string()
        .trim()
        .required(t(TRANSLATION_LOCATION + 'validation_required'))
        .max(
          MAX_SUBJECT_LENGTH,
          t(TRANSLATION_LOCATION + 'validation_too_long')
        ),
      message: Yup.string()
        .trim()
        .required(t(TRANSLATION_LOCATION + 'validation_required'))
        .min(
          MIN_MESSAGE_LENGTH,
          t(TRANSLATION_LOCATION + 'validation_message_min')
        )
        .max(
          MAX_MESSAGE_LENGTH,
          t(TRANSLATION_LOCATION + 'validation_too_long')
        )
    })
  })

  return (
    <>
      <Seo title={t('seo.titles.support')} />
      <PageHeading title={t('seo.titles.support')} />

      <div className='relative max-w-5xl px-4 py-16 sm:px-16'>
        <h1 className='text-xl font-semibold text-gray-900'>
          {t(TRANSLATION_LOCATION + 'support_title')}
        </h1>
        <p className='mt-2 max-w-lg text-sm text-gray-700'>
          {t(TRANSLATION_LOCATION + 'support_description')}
        </p>
        <div className='w-full max-w-xl'>
          <div className='mt-8 max-w-lg'>
            <label className='block text-sm font-medium text-gray-900'>
              {t(TRANSLATION_LOCATION + 'name_label')}
            </label>
            <span className='text-sm text-gray-700'>
              {getName(activeSpace.person)}
            </span>
          </div>
          <div className='mt-4 max-w-lg'>
            <label className='block text-sm font-medium text-gray-900'>
              {t(TRANSLATION_LOCATION + 'email_label')}
            </label>
            <span className='text-sm text-gray-700'>{person_email}</span>
          </div>
          <div className='my-8 px-16'>
            <div className='h-px bg-gray-200' />
          </div>
          {messageSent ? (
            <Success email={person_email} />
          ) : (
            <div>
              <label
                htmlFor='category'
                className='block text-sm font-medium text-gray-700'
              >
                {t(TRANSLATION_LOCATION + 'category_label')}
              </label>
              <select
                id='category'
                name='category'
                value={formik.values.category}
                onChange={formik.handleChange}
                className='mt-2 block w-full rounded-md border-gray-300 text-sm shadow-sm focus:border-blue-500 focus:ring-blue-500'
              >
                {CATEGORIES.map((category) => (
                  <option key={category} value={category}>
                    {t(TRANSLATION_LOCATION + `category_${category}`)}
                  </option>
                ))}
              </select>

              <div className='mt-6'>
                <Input
                  id='subject'
                  label={t(TRANSLATION_LOCATION + 'subject_label')}
                  placeholder={t(TRANSLATION_LOCATION + 'subject_placeholder')}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  value={formik.values.subject}
                  touched={formik.touched.subject}
                  error={formik.errors.subject}
                  disabled={isLoading}
                />
              </div>

              <label
                htmlFor='message'
                className='mt-6 block text-sm font-medium text-gray-700'
              >
                {t(TRANSLATION_LOCATION + 'message_title')}
              </label>
              <div className='mt-2' />
              <Textarea
                id='message'
                formik={formik}
                placeholder={t(TRANSLATION_LOCATION + 'message_placeholder')}
                border
              />

              <div className='mt-4'>
                <label className='flex items-center'>
                  <input
                    type='checkbox'
                    checked={includeLogs}
                    onChange={(e) => setIncludeLogs(e.target.checked)}
                    className='h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500'
                  />
                  <span className='ml-2 text-sm text-gray-700'>
                    {t(TRANSLATION_LOCATION + 'include_logs_label')}
                  </span>
                </label>
                <p className='mt-1 text-xs text-gray-500'>
                  {t(TRANSLATION_LOCATION + 'include_logs_description')}
                </p>
              </div>

              {sendError && (
                <div className='mt-6' role='alert'>
                  <Alert
                    type='error'
                    title={t(TRANSLATION_LOCATION + 'error_title')}
                    description={t(TRANSLATION_LOCATION + 'error_description')}
                  />
                </div>
              )}

              <div className='mt-8 flex gap-4'>
                <Button.PrimaryXL
                  type='submit'
                  onClick={formik.handleSubmit}
                  text={t(TRANSLATION_LOCATION + 'submit_action')}
                  isLoading={isLoading}
                />

                <Button.SecondaryXL
                  onClick={() => {
                    window.location.href = createSupportEmailWithLogs(
                      formik.values.subject || 'Dashboard Support Request',
                      formik.values.message
                    )
                  }}
                  text={t(TRANSLATION_LOCATION + 'mail_action')}
                  className='text-sm'
                />
              </div>
            </div>
          )}
        </div>
        <div
          style={{
            position: 'absolute',
            top: 192,
            right: 0
          }}
          className='hidden xl:block'
        >
          <img className='w-80' alt='Person Heart' src={PersonHeart} />
        </div>
      </div>
    </>
  )
}

export default Support
